import React, { useState } from 'react';
import { X, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useApp } from '../context/AppContext';

const AuthModal = () => {
  const { showAuthModal, setShowAuthModal, authMode, setAuthMode, handleLogin, handleRegister } = useApp();
  const [form, setForm] = useState({ email: '', password: '', firstName: '', lastName: '' });
  const [formError, setFormError] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);
    
    try {
      let result;
      if (authMode === 'login') {
        if (!form.email || !form.password) {
          setFormError('Please fill in all fields');
          setSubmitting(false);
          return;
        }
        result = await handleLogin(form.email, form.password);
      } else {
        if (!form.email || !form.password || !form.firstName || !form.lastName) {
          setFormError('Please fill in all fields');
          setSubmitting(false);
          return;
        }
        if (form.password.length < 8) {
          setFormError('Password must be at least 8 characters');
          setSubmitting(false);
          return;
        }
        result = await handleRegister(form.firstName, form.lastName, form.email, form.password);
      }
      
      if (!result.success) {
        setFormError(result.error);
      } else {
        setForm({ email: '', password: '', firstName: '', lastName: '' });
      }
    } catch (err) {
      setFormError('An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!showAuthModal) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="bg-gradient-to-r from-teal-600 to-emerald-500 p-6 flex justify-between items-center rounded-t-2xl">
          <h2 className="text-2xl font-bold text-white">{authMode === 'login' ? 'Welcome Back' : 'Create Account'}</h2>
          <button onClick={() => { setShowAuthModal(false); setFormError(''); }} className="text-white/80 hover:text-white"><X className="w-6 h-6" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {formError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{formError}</div>}
          {authMode === 'register' && (
            <div className="grid grid-cols-2 gap-4">
              <input type="text" placeholder="First Name" value={form.firstName} onChange={e => setForm({...form, firstName: e.target.value})} className="px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" />
              <input type="text" placeholder="Last Name" value={form.lastName} onChange={e => setForm({...form, lastName: e.target.value})} className="px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" />
            </div>
          )}
          <input type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" />
          <div className="relative">
            <input type={showPass ? 'text' : 'password'} placeholder="Password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 outline-none pr-12" />
            <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">{showPass ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
          </div>
          <button type="submit" disabled={submitting} className="w-full py-4 bg-gradient-to-r from-teal-600 to-emerald-500 text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
            {submitting && <Loader2 className="w-5 h-5 animate-spin" />}
            {authMode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
          <p className="text-center text-slate-600">
            {authMode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button type="button" onClick={() => { setAuthMode(authMode === 'login' ? 'register' : 'login'); setFormError(''); }} className="text-teal-600 font-medium">{authMode === 'login' ? 'Sign Up' : 'Sign In'}</button>
          </p>
        </form>
      </div>
    </div>
  );
};

export default AuthModal;
